import React from 'react';
import TranslateIcon from '@material-ui/icons/Translate';
import { IconButton } from '@material-ui/core';
import { ICategory } from 'pages/categories/interfaces/category.interface';
import {
  ApolloError,
  useMutation,
  QueryLazyOptions,
  OperationVariables,
} from '@apollo/client';
import { Toast } from 'components/Toast';
import { getFriendlyErrorMessage } from 'utils/errorHelper';
import Swal from 'sweetalert2';
import { TRANSLATE_CATEGORIES } from 'queries/category';
import useStyles from 'components/FormContents/components/ActionButtons/styles';

interface ISelectedRows {
  data: {
    index: number;
    dataIndex: number;
  }[];
  lookup: {
    [key: number]: boolean;
  };
}

interface ICategoryTranslate {
  selectedRows: ISelectedRows;
  categories: ICategory[];
  siteDomain: string;
  loadCategories: (
    options?: QueryLazyOptions<OperationVariables> | undefined,
  ) => void;
}

const filteredData = (selectedRows: ISelectedRows, categories: ICategory[]) => {
  const ids: string[] = [];

  selectedRows.data.forEach((data) => {
    const category = categories[data.dataIndex] || null;
    if (category) ids.push(category.id);
  });

  return ids;
};

export const CategoryTranslate: React.FC<ICategoryTranslate> = ({
  selectedRows,
  categories,
  siteDomain,
  loadCategories,
}) => {
  const classes = useStyles();

  const selectedCategories = filteredData(selectedRows, categories);

  const handleOnCompletedMutation = () => {
    Toast('Action executed with success!');

    loadCategories({
      variables: {
        siteDomain,
        reload: true,
      },
    });
  };

  const handleOnErrorMutation = (error: ApolloError) => {
    Toast(getFriendlyErrorMessage(error), 'error');
  };

  const [translateCategories] = useMutation(TRANSLATE_CATEGORIES, {
    onCompleted: handleOnCompletedMutation,
    onError: handleOnErrorMutation,
    variables: {
      siteDomain,
      categories: selectedCategories,
    },
  });

  const confirmAlert = async (subtitle: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: subtitle,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#78001b',
      reverseButtons: true,
      confirmButtonText: 'Confirm',
      customClass: {
        container: classes.swalDialog,
      },
    });
    return result.value;
  };

  const createTranslation = async () => {
    if (
      await confirmAlert(
        `Do you want to translate this categories?
        <br>
        <span style="font-size: 14px; padding-top: 14px; display: inline-block; color: red;">
          Note: The categories will be translated into the languages that are in the site settings.
        </span>`,
      )
    ) {
      translateCategories();
    }
  };

  return (
    <IconButton onClick={() => createTranslation()}>
      <TranslateIcon />
    </IconButton>
  );
};
